import moment from "moment-timezone";

export default class CalendarEvent {
  activityNote: string;
  assignedTo: string;
  assignedToFullName: string;
  assignedToUsername: string;
  day: number;
  disciplineCode: string;
  role: string;
  eventId: string;
  month: number;
  patientChartId: number;
  patientName: string;
  patientFirstName: string;
  patientLastName: string;
  patientMiddleInitial?: string;
  recurringEventId: string;
  timezone: string;
  userId: string;
  visitId: number;
  status: string;
  visitType: string;
  visitTypeName: string;
  week: number;
  year: number;
  missedVisit: boolean;
  missedVisitReason: string;
  missedVisitDescription: string;
  tenantId: number;
  careLocation: string;
  zipCode: string;
  // version: number;
  // history: string;
  startDatetime: Date;
  endDatetime: Date;
  createdBy: string;
  createdDatetime: Date;
  updatedBy: string;
  updatedDatetime: Date;
  patientPhone: string;
  isDelete: boolean;
  // prn: boolean;
  activity: string;
  eventType: string;
  dataType: string;
  orderId: number;
  orderCreatedDate: string;
  orderDescription: string;
  orderProvider: string;
  orderStartDate: Date;
  orderDiscontinueDate: Date;
  sectionType: string = "Resource";
  hopeFlags?: string[];
  isHuv1?: boolean = false;
  isHuv2?: boolean = false;
  isSfv?: boolean = false;
  prnReason?: string;

  constructor() {
    this.startDatetime = new Date();
    this.endDatetime = moment(this.startDatetime).add(30, "m").toDate();
  }

  public toString = (): string => {
    return `CalendarEvent (eventId: ${this.eventId}, patient: ${this.patientChartId}, start: ${this.startDatetime}, end: ${this.endDatetime})`;
  };

  /**
   * Maps a CalendarEvent to a DHTML Scheduler Event
   */
  mapToDhtmlSchedulerEvent(addPatientSection: boolean = false): any {
    // const visitTypeName = this.getVisitTypeName(this.visitType);
    const finalStatus = this.status === "" ? "SCHEDULED" : this.status;
    const finalCareLocation =
      this.careLocation === "" ? "No Care Location" : this.careLocation;
    const finalZipCode = this.zipCode === "" ? "00000" : this.zipCode;
    const finalActivity = this.activity === "" ? "" : this.activity;
    const sectionId = addPatientSection
      ? this.patientChartId.toString()
      : this.userId;
    return {
      activityNote: this.activityNote,
      assignedTo: this.assignedTo,
      assignedToFullName: this.assignedToFullName,
      assignedToUsername: this.assignedToUsername,
      day: this.day,
      disciplineCode: this.disciplineCode,
      role: this.role,
      eventId: this.eventId,
      month: this.month,
      patientChartId: this.patientChartId,
      patientName: this.patientName,
      patientFirstName: this.patientFirstName,
      patientLastName: this.patientLastName,
      patientMiddleInitial: this.patientMiddleInitial,
      recurringEventId: this.recurringEventId,
      timezone: this.timezone,
      userId: this.userId,
      section_id: sectionId,
      patientHeaderType: false,
      visitId: this.visitId,
      status: finalStatus,
      visitType: this.visitType,
      visitTypeName: this.visitTypeName,
      week: this.week,
      year: this.year,
      missedVisit: this.missedVisit,
      missedVisitReason: this.missedVisitReason,
      missedVisitDescription: this.missedVisitDescription,
      zipCode: finalZipCode,
      careLocation: finalCareLocation,
      // 'version': this.version,
      // 'history': this.history,
      createdBy: this.createdBy,
      createdDatetime: this.createdDatetime,
      updatedBy: this.updatedBy,
      updatedDatetime: this.updatedDatetime,
      start_date: this.startDatetime,
      end_date: this.endDatetime,
      startDatetime: this.startDatetime,
      endDatetime: this.endDatetime,
      text:
        this.eventType &&
        (this.eventType === "ACTIVITY" || this.eventType === "HOPE")
          ? finalActivity
          : this.visitTypeName +
            " - " +
            finalCareLocation +
            "<br/>" +
            finalZipCode +
            " - " +
            finalStatus +
            "<br/>" +
            this.patientPhone,
      patientPhone: this.patientPhone,
      isDelete: this.isDelete,
      activity: this.activity,
      eventType: this.eventType,
      orderId: this.orderId,
      orderCreatedDate: this.orderCreatedDate,
      orderDescription: this.orderDescription,
      orderProvider: this.orderProvider,
      orderStartDate: this.orderStartDate,
      orderDiscontinueDate: this.orderDiscontinueDate,
      sectionType: this.sectionType,
      hopeFlags: this.hopeFlags,
      isHuv1: this.isHuv1,
      isHuv2: this.isHuv2,
      isSfv: this.isSfv,
    };
  }

  static mapCalendarEvent(id: string, dhtml: any): CalendarEvent {
    let ce = new CalendarEvent();
    ce.activityNote = dhtml.activityNote;
    ce.assignedTo = dhtml.assignedTo;
    ce.assignedToFullName = dhtml.assignedToFullName;
    ce.assignedToUsername = dhtml.assignedToUsername;
    ce.disciplineCode = dhtml.disciplineCode;
    ce.role = dhtml.role;
    ce.eventId = dhtml.eventId;
    ce.eventType = dhtml.eventType;
    ce.patientChartId = dhtml.patientChartId;
    ce.patientName = dhtml.patientName;
    ce.patientFirstName = dhtml.patientFirstName;
    ce.patientLastName = dhtml.patientLastName;
    ce.patientMiddleInitial = dhtml.patientMiddleInitial;
    ce.recurringEventId = dhtml.recurringEventId;
    ce.userId = dhtml.userId;
    ce.visitId = dhtml.visitId;
    ce.status = dhtml.status;
    ce.visitType = dhtml.visitType;
    ce.visitTypeName = dhtml.visitTypeName;
    ce.day = dhtml.day;
    ce.week = dhtml.week;
    ce.month = dhtml.month;
    ce.year = dhtml.year;
    ce.timezone = dhtml.timezone;
    ce.startDatetime = dhtml.start_date;
    ce.endDatetime = dhtml.end_date;
    ce.missedVisit = dhtml.missedVisit;
    ce.missedVisitReason = dhtml.missedVisitReason;
    ce.missedVisitDescription = dhtml.missedVisitDescription;
    ce.careLocation = dhtml.careLocation;
    ce.zipCode = dhtml.zipCode;
    // ce.version = dhtml.version;
    // ce.history = dhtml.history;
    ce.patientPhone = dhtml.patientPhone;

    ce.createdBy = dhtml.createdBy;
    ce.createdDatetime = dhtml.createdDatetime;
    ce.updatedBy = dhtml.updatedBy;
    ce.updatedDatetime = dhtml.updatedDatetime;
    ce.isDelete = dhtml.isDelete;
    ce.orderId = dhtml.orderId;
    ce.orderCreatedDate = dhtml.orderCreatedDate;
    ce.orderDescription = dhtml.orderDescription;
    ce.orderProvider = dhtml.orderProvider;
    ce.orderStartDate = dhtml.orderStartDate;
    ce.orderDiscontinueDate = dhtml.orderDiscontinueDate;
    return ce;
  }
}

// getVisitTypeName(visitType): string {
//   switch (visitType) {
//     case 'CL003.17': {
//       this.visitTypeName = 'Palliative Care';
//       break;
//     }
//     case 'CL003.18': {
//       this.visitTypeName = 'Evaluation';
//       break;
//     }
//     case 'CL003.1': {
//       this.visitTypeName = 'Initial/Comprehensive Assessment';
//       break;
//     }
//     case 'CL003.2': {
//       this.visitTypeName = 'Comprehensive Assessment';
//       break;
//     }
//     case 'CL003.3': {
//       this.visitTypeName = 'Routine';
//       break;
//     }
//     case 'CL003.4': {
//       this.visitTypeName = 'Face to Face Visit';
//       break;
//     }
//     case 'CL003.5': {
//       this.visitTypeName = 'Continuous Care Visits';
//       break;
//     }
//     case 'CL003.6': {
//       this.visitTypeName = 'Emergent';
//       break;
//     }
//     case 'CL003.7': {
//       this.visitTypeName = 'In-Patient';
//       break;
//     }
//     case 'CL003.8': {
//       this.visitTypeName = 'Watch Care Visit';
//       break;
//     }
//     case 'CL003.9': {
//       this.visitTypeName = 'EMC';
//       break;
//     }
//     case 'CL003.10': {
//       this.visitTypeName = 'Phone Call';
//       break;
//     }
//     case 'CL003.11': {
//       this.visitTypeName = 'Supervisory Visit';
//       break;
//     }
//     case 'CL003.12': {
//       this.visitTypeName = 'Initial Nursing Assessment (HIS)';
//       break;
//     }
//     case 'CL003.13': {
//       this.visitTypeName = 'Hospice House Comprehensive';
//       break;
//     }
//     case 'CL003.14': {
//       this.visitTypeName = 'Hospice House Encounter';
//       break;
//     }
//     case 'CL003.15': {
//       this.visitTypeName = 'New Patient/Admit';
//       break;
//     }
//     case 'CL003.16': {
//       this.visitTypeName = 'Established';
//       break;
//     }
//     default: {
//       this.visitTypeName = 'Visit Type Not Selected';
//       break;
//     }
//   }
//   return this.visitTypeName;
// }



// WEBPACK FOOTER //
// ./src/models/calendar/calendar-event.ts